"use client";

import { SectionFrame } from "@/components/ui/section-frame";
import {
  SCROLL_COLUMN_REVEAL_ENTER_DEG,
  SCROLL_COLUMN_REVEAL_OPACITY_EASE_MS,
  SCROLL_COLUMN_REVEAL_ROOT_MARGIN,
  SCROLL_COLUMN_REVEAL_SPRING_DAMPING,
  SCROLL_COLUMN_REVEAL_SPRING_STIFFNESS,
  SCROLL_COLUMN_REVEAL_THRESHOLD,
  easeOutCubic,
  easeOutOpacity,
  scrollColumnLeftRotateDeg,
  scrollColumnRightRotateDeg,
  stepScrollColumnRevealSpring,
  useScrollColumnReveal,
} from "@/hooks/use-scroll-column-reveal";

/** @deprecated Prefer SCROLL_COLUMN_REVEAL_* — kept for existing tests/imports. */
export const ATTENTION_ENTER_DEG = SCROLL_COLUMN_REVEAL_ENTER_DEG;
export const ATTENTION_SPRING_STIFFNESS = SCROLL_COLUMN_REVEAL_SPRING_STIFFNESS;
export const ATTENTION_SPRING_DAMPING = SCROLL_COLUMN_REVEAL_SPRING_DAMPING;
export const ATTENTION_OPACITY_EASE_MS = SCROLL_COLUMN_REVEAL_OPACITY_EASE_MS;
export const ATTENTION_IN_VIEW_ROOT_MARGIN = SCROLL_COLUMN_REVEAL_ROOT_MARGIN;
export const ATTENTION_IN_VIEW_THRESHOLD = SCROLL_COLUMN_REVEAL_THRESHOLD;
export {
  easeOutCubic,
  easeOutOpacity,
  stepScrollColumnRevealSpring as stepAttentionSpring,
  scrollColumnLeftRotateDeg as attentionLeftRotateDeg,
  scrollColumnRightRotateDeg as attentionRightRotateDeg,
};

export function AttentionSection() {
  const { ref, inView, leftRotate, rightRotate, opacity } = useScrollColumnReveal();

  return (
    <div data-testid="attention-section">
      <SectionFrame
        startColor="rgb(var(--cta-yellow-bright))"
        endColor="rgb(var(--cta-orange))"
        flipped={true}
        clipOverflowX={false}
        contentClassName="py-16"
      >
        {/* Observe the columns (not the SVG borders) so the reveal starts
            when the copy/art enter the trigger band — not off-screen. */}
        <div
          ref={ref}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center"
          data-in-view={inView ? "true" : "false"}
        >
          {/* Left Column - Icon and Text: pivot bottom-right, swings in from left */}
          <div
            className="flex flex-col items-start text-left origin-bottom-right will-change-transform"
            data-testid="attention-copy"
            style={{ transform: `rotate(${leftRotate}deg)`, opacity }}
          >
            <img
              src="/images/marketing/home/attention/shield-icon.svg"
              alt="Shield icon"
              className="w-12 h-12 lg:w-16 lg:h-16 mb-4"
            />
            <h2 className="text-2xl md:text-3xl lg:text-4xl text-black mb-6">
              Shield and build attention span
            </h2>
            <p className="text-black/90 text-lg lg:text-xl/8 mb-6">
              Our brains weren&apos;t built to binge 8-second dopamine hits. Kids and adults today
              have many resulting issues with not only their education, but their behavior and
              mental health as well. PlayMath.org stretches the brain out again and sparks a love of
              juicy and &ldquo;desirably-difficult&rdquo; cognitive tasks, setting our brains back
              on course to do what they were designed to do.
            </p>
            <a
              href="https://www.researchgate.net/publication/387697885_THE_IMPACT_OF_TIKTOK'S_FAST-PACED_CONTENT_ON_ATTENTION_SPAN_OF_STUDENTS"
              target="_blank"
              rel="noopener noreferrer"
              className="text-black/70 text-base lg:text-lg hover:text-black underline"
            >
              Read the studies on the impact of social media on brains ➜
            </a>
          </div>

          {/* Right Column - Art: pivot bottom-left, swings in from right */}
          <div
            className="flex justify-center lg:justify-end origin-bottom-left will-change-transform"
            data-testid="attention-art"
            style={{ transform: `rotate(${rightRotate}deg)`, opacity }}
          >
            <img
              src="/images/marketing/home/attention/shielded-art.webp"
              alt="Shielded brain illustration"
              className="w-full max-w-md lg:max-w-lg xl:max-w-xl"
            />
          </div>
        </div>
      </SectionFrame>
    </div>
  );
}

export default AttentionSection;
